import React from "react";
import {
  getSafeBlogImageUrl,
  normalizeBlogImageAlignment,
  normalizeBlogImageWidth,
  parseBlogImageMarkdown,
  parseBlogVideoMarkdown,
  resolveBlogVideo,
} from "./blogMedia";

const safeLink = (value) =>
  /^(https?:\/\/|mailto:)[^\s<>()]+$/i.test(String(value || "").trim());

const findClosingMarker = (value, marker, fromIndex, isValid) => {
  let index = value.indexOf(marker, fromIndex);
  while (index >= 0) {
    if (!isValid || isValid(index)) return index;
    index = value.indexOf(marker, index + 1);
  }
  return -1;
};

/** Render the small, intentionally safe Markdown subset produced by BlogEditor. */
const renderInline = (text, keyPrefix, depth = 0) => {
  const value = String(text || "");
  if (!value || depth > 12) return value;

  const nodes = [];
  let plainText = "";
  let cursor = 0;

  const flushText = () => {
    if (!plainText) return;
    nodes.push(plainText);
    plainText = "";
  };

  const appendNode = (node, nextCursor) => {
    flushText();
    nodes.push(node);
    cursor = nextCursor;
  };

  while (cursor < value.length) {
    const key = `${keyPrefix}-${nodes.length}`;

    if (value[cursor] === "[") {
      const labelEnd = value.indexOf("](", cursor + 1);
      const linkEnd = labelEnd >= 0 ? value.indexOf(")", labelEnd + 2) : -1;
      if (labelEnd > cursor + 1 && linkEnd > labelEnd + 2) {
        const label = value.slice(cursor + 1, labelEnd);
        const href = value.slice(labelEnd + 2, linkEnd);
        const source = value.slice(cursor, linkEnd + 1);
        if (safeLink(href)) {
          const external = /^https?:\/\//i.test(href);
          appendNode(
            <a
              key={key}
              href={href}
              {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
            >
              {renderInline(label, key, depth + 1)}
            </a>,
            linkEnd + 1
          );
          continue;
        }
        plainText += source;
        cursor = linkEnd + 1;
        continue;
      }
    }

    if (value[cursor] === "`") {
      const end = value.indexOf("`", cursor + 1);
      if (end > cursor + 1 && !value.slice(cursor + 1, end).includes("\n")) {
        appendNode(<code key={key}>{value.slice(cursor + 1, end)}</code>, end + 1);
        continue;
      }
    }

    if (value.startsWith("***", cursor) && !/\s/.test(value[cursor + 3] || "")) {
      const markerStart = cursor;
      const end = findClosingMarker(
        value,
        "***",
        cursor + 3,
        (index) => index > markerStart + 3 && !/\s/.test(value[index - 1])
      );
      if (end >= 0 && !value.slice(cursor + 3, end).includes("\n")) {
        appendNode(
          <strong key={key}>
            <em>{renderInline(value.slice(cursor + 3, end), key, depth + 1)}</em>
          </strong>,
          end + 3
        );
        continue;
      }
    }

    if (value.startsWith("**", cursor) && !/\s/.test(value[cursor + 2] || "")) {
      const markerStart = cursor;
      const end = findClosingMarker(
        value,
        "**",
        cursor + 2,
        (index) => index > markerStart + 2 && !/\s/.test(value[index - 1])
      );
      if (end >= 0 && !value.slice(cursor + 2, end).includes("\n")) {
        appendNode(
          <strong key={key}>
            {renderInline(value.slice(cursor + 2, end), key, depth + 1)}
          </strong>,
          end + 2
        );
        continue;
      }
    }

    const underlineCanOpen =
      value.startsWith("++", cursor) &&
      !/[\w+]/.test(value[cursor - 1] || "") &&
      !/\s/.test(value[cursor + 2] || "");
    if (underlineCanOpen) {
      const markerStart = cursor;
      const end = findClosingMarker(
        value,
        "++",
        cursor + 2,
        (index) =>
          index > markerStart + 2 &&
          !/\s/.test(value[index - 1]) &&
          value[index + 2] !== "+"
      );
      if (end >= 0 && !value.slice(cursor + 2, end).includes("\n")) {
        appendNode(
          <u key={key}>{renderInline(value.slice(cursor + 2, end), key, depth + 1)}</u>,
          end + 2
        );
        continue;
      }
    }

    if (value.startsWith("~~", cursor) && !/\s/.test(value[cursor + 2] || "")) {
      const markerStart = cursor;
      const end = findClosingMarker(
        value,
        "~~",
        cursor + 2,
        (index) => index > markerStart + 2 && !/\s/.test(value[index - 1])
      );
      if (end >= 0 && !value.slice(cursor + 2, end).includes("\n")) {
        appendNode(
          <s key={key}>{renderInline(value.slice(cursor + 2, end), key, depth + 1)}</s>,
          end + 2
        );
        continue;
      }
    }

    const italicCanOpen =
      value[cursor] === "*" &&
      value[cursor + 1] !== "*" &&
      !/\s/.test(value[cursor + 1] || "");
    if (italicCanOpen) {
      const markerStart = cursor;
      const end = findClosingMarker(
        value,
        "*",
        cursor + 1,
        (index) =>
          index > markerStart + 1 &&
          value[index - 1] !== "*" &&
          value[index + 1] !== "*" &&
          !/\s/.test(value[index - 1])
      );
      if (end >= 0 && !value.slice(cursor + 1, end).includes("\n")) {
        appendNode(
          <em key={key}>{renderInline(value.slice(cursor + 1, end), key, depth + 1)}</em>,
          end + 1
        );
        continue;
      }
    }

    plainText += value[cursor];
    cursor += 1;
  }

  flushText();
  return nodes;
};

const headingId = (text, index) => {
  const slug = String(text || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return `${slug || "section"}-${index + 1}`;
};

export const createArticleBlocks = (content = "") => {
  const lines = String(content).replace(/\r/g, "").split("\n");
  const blocks = [];
  let paragraph = [];

  const flushParagraph = () => {
    if (!paragraph.length) return;
    blocks.push({ type: "paragraph", text: paragraph.join(" ").trim() });
    paragraph = [];
  };

  for (let index = 0; index < lines.length; index += 1) {
    const trimmed = lines[index].trim();
    if (!trimmed) {
      flushParagraph();
      continue;
    }

    const heading = trimmed.match(/^(#{1,3})\s+(.+)$/);
    const image = parseBlogImageMarkdown(trimmed);
    const video = parseBlogVideoMarkdown(trimmed);
    const rejectedMedia = /^!\[|^@\[video\]\(/i.test(trimmed);
    const unordered = trimmed.match(/^[-*]\s+(.+)$/);
    const ordered = trimmed.match(/^\d+[.)]\s+(.+)$/);

    if (heading) {
      flushParagraph();
      const level = heading[1].length >= 3 ? 3 : 2;
      blocks.push({
        type: "heading",
        level,
        text: heading[2],
        id: headingId(heading[2], blocks.length),
      });
    } else if (image) {
      flushParagraph();
      blocks.push({ type: "image", ...image });
    } else if (video) {
      flushParagraph();
      blocks.push({ type: "video", ...video });
    } else if (rejectedMedia) {
      flushParagraph();
      blocks.push({ type: "literal", text: trimmed });
    } else if (trimmed === "---") {
      flushParagraph();
      blocks.push({ type: "divider" });
    } else if (trimmed.startsWith("> ")) {
      flushParagraph();
      const text = [];
      while (index < lines.length && lines[index].trim().startsWith("> ")) {
        text.push(lines[index].trim().slice(2));
        index += 1;
      }
      index -= 1;
      blocks.push({ type: "quote", text: text.join(" ") });
    } else if (unordered || ordered) {
      flushParagraph();
      const type = unordered ? "unordered-list" : "ordered-list";
      const items = [];
      const matcher = unordered ? /^[-*]\s+(.+)$/ : /^\d+[.)]\s+(.+)$/;
      while (index < lines.length) {
        const match = lines[index].trim().match(matcher);
        if (!match) break;
        items.push(match[1]);
        index += 1;
      }
      index -= 1;
      blocks.push({ type, items });
    } else {
      paragraph.push(trimmed);
    }
  }

  flushParagraph();
  return blocks;
};

export const extractArticleHeadings = (content) =>
  createArticleBlocks(content).filter((block) => block.type === "heading");

const ArticleContent = ({ content, className = "" }) => {
  const blocks = createArticleBlocks(content);

  return (
    <div className={`blog-article-content ${className}`.trim()}>
      {blocks.map((block, index) => {
        const key = `${block.type}-${index}`;
        if (block.type === "heading") {
          const Heading = block.level === 3 ? "h3" : "h2";
          return <Heading id={block.id} key={key}>{renderInline(block.text, key)}</Heading>;
        }
        if (block.type === "quote") {
          return <blockquote key={key}>{renderInline(block.text, key)}</blockquote>;
        }
        if (block.type === "divider") return <hr key={key} />;
        if (block.type === "literal") return <p key={key}>{block.text}</p>;
        if (block.type === "image") {
          const src = getSafeBlogImageUrl(block.src);
          if (!src) return <p key={key}>{block.src}</p>;
          const width = normalizeBlogImageWidth(block.width);
          const align = normalizeBlogImageAlignment(block.align);
          return (
            <figure
              className={`blog-article-media blog-article-media--image blog-article-media--width-${width} blog-article-media--align-${align}`}
              key={key}
            >
              <img src={src} alt={block.alt} loading="lazy" decoding="async" />
              {block.caption && <figcaption>{block.caption}</figcaption>}
            </figure>
          );
        }
        if (block.type === "video") {
          const video = resolveBlogVideo(block.url);
          if (!video) return <p key={key}>{block.url}</p>;
          const title = `${video.provider} video${block.caption ? `: ${block.caption}` : ""}`;
          return (
            <figure
              className="blog-article-media blog-article-media--video blog-article-media--width-large blog-article-media--align-center"
              key={key}
            >
              <div className="blog-article-video">
                <iframe
                  src={video.embedUrl}
                  title={title}
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                  sandbox="allow-scripts allow-same-origin allow-presentation"
                />
              </div>
              {block.caption && <figcaption>{block.caption}</figcaption>}
            </figure>
          );
        }
        if (block.type === "unordered-list" || block.type === "ordered-list") {
          const List = block.type === "ordered-list" ? "ol" : "ul";
          return (
            <List key={key}>
              {block.items.map((item, itemIndex) => (
                <li key={`${key}-${itemIndex}`}>{renderInline(item, `${key}-${itemIndex}`)}</li>
              ))}
            </List>
          );
        }
        return <p key={key}>{renderInline(block.text, key)}</p>;
      })}
    </div>
  );
};

export default ArticleContent;

